<template>
  <div class="connections-graph">
    <div class="graph-container" ref="graphContainer">
      <!-- Graph will be rendered here by D3 -->
    </div>
    
    <!-- Node Details Modal with improved styling - no dark overlay -->
    <div v-if="showModal" class="fixed z-50" :style="{ left: modalPosition.x + 'px', top: modalPosition.y + 'px' }">
      <div data-v-3a7bea10="" class="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div data-v-3a7bea10="" class="card-header">
          <h3 data-v-3a7bea10="">{{ selectedNode.name || selectedNode.id }}</h3>
        </div>
        
        <div data-v-3a7bea10="" class="card-body">
          <!-- Farm node details -->
          <template v-if="selectedNode.type === 'farm'">
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">P I C:</strong> {{ selectedNode.pic || selectedNode.PIC || 'N/A' }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Farm Name:</strong> {{ selectedNode.name || selectedNode.FarmName || 'N/A' }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Region:</strong> {{ selectedNode.region || selectedNode.Region || 'N/A' }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">L G A:</strong> {{ selectedNode.LGA || 'N/A' }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Owner Name:</strong> {{ selectedNode.OwnerName || 'N/A' }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Sustaina Wool:</strong> {{ selectedNode.SustainaWool || 'No' }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">State:</strong> {{ selectedNode.State || '-' }}</div>
          </template>
          
          <!-- Wool line details -->
          <template v-else-if="selectedNode.type === 'wool_line'">
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Line ID:</strong> {{ selectedNode.lineId || selectedNode.LineID || 'N/A' }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Farm:</strong> {{ selectedNode.farmName || selectedNode.FarmName || 'N/A' }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Micron:</strong> {{ selectedNode.micron || selectedNode.Micron_um || 'N/A' }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Yield:</strong> {{ selectedNode.yield || selectedNode.Yield_pct ? (selectedNode.yield || selectedNode.Yield_pct) + '%' : 'N/A' }}</div>
          </template>
          
          <!-- Commercial entity details -->
          <template v-else-if="isCommercialEntity(selectedNode.type)">
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Name:</strong> {{ selectedNode.name || 'N/A' }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Type:</strong> {{ formatNodeType(selectedNode.type) }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Location:</strong> {{ selectedNode.location || 'N/A' }}</div>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Code:</strong> {{ selectedNode.code || 'N/A' }}</div>
            <div data-v-3a7bea10="" v-if="selectedNode.certification" class="detail-item"><strong data-v-3a7bea10="">Certification:</strong> {{ selectedNode.certification }}</div>
          </template>
          
          <!-- Generic fields for all other node types -->
          <template v-else>
            <div data-v-3a7bea10="" class="detail-item"><strong data-v-3a7bea10="">Type:</strong> {{ formatNodeType(selectedNode.type) }}</div>
            <template v-for="(value, key) in selectedNode" :key="key">
              <div data-v-3a7bea10="" v-if="value && !['id', 'name', 'type', 'pic', 'region', 'lineId', 'farmName', 'micron', 'yield', 'location', 'code', 'certification', 'x', 'y', 'index', 'vx', 'vy', 'fx', 'fy', 'level'].includes(key)" class="detail-item">
                <strong data-v-3a7bea10="">{{ formatFieldName(key) }}:</strong> {{ value }}
              </div>
            </template>
          </template>
          
          <div data-v-3a7bea10="" class="card-source">
            Source: {{ getNodeSource(selectedNode.type) }}
          </div>
        </div>
        
        <div data-v-3a7bea10="" class="card-footer">
          <button 
            data-v-3a7bea10="" 
            class="details-btn" 
            @click="closeModal"
          >
            Close
          </button>
          <button 
            v-if="selectedNode.id && canNavigateToNode(selectedNode)" 
            data-v-3a7bea10="" 
            class="details-btn ml-2" 
            @click="navigateToNode(selectedNode)"
          >
            View Details
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';

export default {
  name: 'ConnectionsGraph',
  props: {
    rootEntity: {
      type: Object,
      required: true
    },
    connections: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      simulation: null,
      svg: null,
      nodes: [],
      links: [],
      modalPosition: { x: 0, y: 0 },
      showModal: false,
      selectedNode: {},
      nodeTypes: {
        farm: { color: '#3A895F', icon: 'M12 21.7L3.7 13.4C2.5 12.2 1.8 10.3 1.8 8.3c0-4.4 3.6-8 8-8s8 3.6 8 8c0 2-0.7 3.9-1.9 5.1L12 21.7z' },
        wool_line: { color: '#F59E0B', icon: 'M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z' },
        classer: { color: '#3B82F6', icon: 'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' },
        broker: { color: '#F97316', icon: 'M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z' },
        auction_house: { color: '#EF4444', icon: 'M3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2zm12 10H9v-2h6v2zM5 5h14v2H5V5zm0 14v-2h14v2H5z' },
        testing_lab: { color: '#6366F1', icon: 'M19.8 18.4L14 10.67V6.5l1.35-1.69c.26-.33.03-.81-.39-.81H9.04c-.42 0-.65.48-.39.81L10 6.5v4.17L4.2 18.4c-.49.66-.02 1.6.8 1.6h14c.82 0 1.29-.94.8-1.6z' },
        shipping_company: { color: '#14B8A6', icon: 'M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zm-.5 1.5l1.96 2.5H17V9.5h2.5zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm2.22-3c-.55-.61-1.33-1-2.22-1s-1.67.39-2.22 1H3V6h12v9H8.22zM18 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z' },
        factory: { color: '#8B5CF6', icon: 'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z' },
        brand: { color: '#EC4899', icon: 'M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58s1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41s-.23-1.06-.59-1.42zM13 20.01L4 11V4h7v-.01l9 9-7 7.02z' },
        certification_body: { color: '#10B981', icon: 'M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z' },
        government_agency: { color: '#6B7280', icon: 'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z' },
        port: { color: '#0369A1', icon: 'M20 12c0-1.1-.9-2-2-2V7c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v3c-1.1 0-2 .9-2 2v5h1.33L6 19h1l.67-2h8.67l.66 2h1l.67-2H20v-5zm-4-2H8V7h8v3z' },
        default: { color: '#9CA3AF', icon: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z' }
      }
    };
  },
  mounted() {
    this.initGraph();
  },
  watch: {
    rootEntity: {
      handler() {
        this.updateGraph();
      },
      deep: true
    },
    connections: {
      handler() {
        this.updateGraph();
      },
      deep: true
    }
  },
  methods: {
    initGraph() {
      const container = this.$refs.graphContainer;
      const width = container.clientWidth;
      const height = 600;
      
      // Create SVG
      this.svg = d3.select(container)
        .append('svg')
        .attr('width', width)
        .attr('height', height)
        .attr('viewBox', [0, 0, width, height])
        .attr('style', 'max-width: 100%; height: auto;');
      
      // Create container for the graph
      const g = this.svg.append('g');
      
      // Add zoom behavior
      this.svg.call(d3.zoom()
        .extent([[0, 0], [width, height]])
        .scaleExtent([0.5, 5])
        .on('zoom', (event) => {
          g.attr('transform', event.transform);
        }));
      
      // Prepare the data
      this.updateGraph();
    },
    
    updateGraph() {
      if (!this.svg) return;
      
      // Clear previous graph
      this.svg.selectAll('*').remove();
      
      // Process data
      this.processGraphData();
      
      const width = this.$refs.graphContainer.clientWidth;
      const height = 600;
      
      // Create container for the graph
      const g = this.svg.append('g');
      
      // Create links
      const link = g.append('g')
        .selectAll('.link')
        .data(this.links)
        .join('line')
        .attr('class', 'link')
        .attr('stroke', '#3A895F')
        .attr('stroke-opacity', 0.7)
        .style('opacity', 0.7)
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', d => d.dashed ? '5,5' : 'none')
        .attr('class', d => d.dashed ? 'dashed-link' : 'solid-link');
      
      // Create node groups
      const node = g.append('g')
        .selectAll('.node')
        .data(this.nodes)
        .join('g')
        .attr('class', 'node')
        .style('cursor', 'pointer')
        .call(this.drag(this.simulation))
        .on('click', (event, d) => {
          this.showNodeDetails(d, event);
          event.stopPropagation();
        });
      
      // Store reference to this for use in callbacks
      const self = this;
      
      // Add nodes with shapes and icons based on type
      node.each(function(d) {
        const nodeEl = d3.select(this);
        const nodeType = d.type;
        
        // Determine if this is a root node
        const isRootNode = nodeType === 'farm';
        
        // Determine if this is a commercial entity (should be square)
        const isCommercialEntity = nodeType === 'testing_lab' || nodeType === 'auction_house' || 
                                  nodeType === 'shipping_company' || nodeType === 'port' || 
                                  nodeType === 'factory' || nodeType === 'broker';
        
        if (isRootNode) {
          // Root nodes are larger circles
          nodeEl.append("circle")
            .attr("r", 25)
            .attr("fill", "#3A895F")
            .attr("stroke", "#fff")
            .attr("stroke-width", 3)
            .style("filter", "drop-shadow(0px 2px 4px rgba(0,0,0,0.2))");
        } else if (isCommercialEntity) {
          // Commercial entities are rounded squares
          nodeEl.append("rect")
            .attr("width", 40)
            .attr("height", 40)
            .attr("x", -20)
            .attr("y", -20)
            .attr("fill", "#3A895F")
            .attr("stroke", "#fff")
            .attr("stroke-width", 2)
            .attr("rx", 4)
            .attr("ry", 4)
            .style("filter", "drop-shadow(0px 2px 4px rgba(0,0,0,0.2))");
        } else {
          // All other entities are smaller circles (classer, wool_line, etc.)
          nodeEl.append("circle")
            .attr("r", 15)
            .attr("fill", "#3A895F")
            .attr("stroke", "#fff")
            .attr("stroke-width", 2)
            .style("filter", "drop-shadow(0px 2px 4px rgba(0,0,0,0.2))");
        }
        
        // Add emoji icon
        const iconMap = {
          'farm': '🐑',
          'wool_line': '📦',
          'classer': '👤',
          'broker': '💼',
          'auction_house': '🏛️',
          'testing_lab': '🔬',
          'shipping_company': '🚢',
          'port': '⚓',
          'factory': '🏭',
          'certification_body': '✓',
          'government_agency': '🏛️'
        };
        
        nodeEl.append("text")
          .attr("dy", ".35em")
          .attr("text-anchor", "middle")
          .style("font-size", isRootNode ? "20px" : (isCommercialEntity ? "18px" : "16px"))
          .text(iconMap[d.type] || '📦');
      });
      
      // Add node labels
      node.append('text')
        .attr('dy', d => this.getNodeSize(d) + 12)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('fill', '#333')
        .text(d => this.getNodeLabel(d));
      
      // Add hover effects
      node.on('mouseenter', function(event, d) {
        const nodeGroup = d3.select(this);
        const nodeType = d.type;
        const isRootNode = nodeType === 'farm';
        const isCommercialEntity = nodeType === 'testing_lab' || nodeType === 'auction_house' || 
                                  nodeType === 'shipping_company' || nodeType === 'port' || 
                                  nodeType === 'factory' || nodeType === 'broker';
        
        // Scale up the node
        if (nodeGroup.select('circle').size() > 0) {
          // For circle nodes
          const currentRadius = isRootNode ? 25 : 15;
          nodeGroup.select('circle')
            .transition()
            .duration(200)
            .attr('r', currentRadius + 3);
        } else if (nodeGroup.select('rect').size() > 0) {
          // For rectangle nodes
          nodeGroup.select('rect')
            .transition()
            .duration(200)
            .attr('width', 46)
            .attr('height', 46)
            .attr('x', -23)
            .attr('y', -23);
        }
        
        // Highlight connected links
        d3.selectAll('.link')
          .transition()
          .duration(200)
          .style('opacity', link => {
            return (link.source.id === d.id || link.target.id === d.id) ? 1 : 0.2;
          })
          .style('stroke-width', link => {
            return (link.source.id === d.id || link.target.id === d.id) ? 3 : 2;
          });
        
        // Dim other nodes
        d3.selectAll('.node')
          .transition()
          .duration(200)
          .style('opacity', node => node.id === d.id ? 1 : 0.3);
      })
      .on('mouseleave', function(event, d) {
        const nodeGroup = d3.select(this);
        const nodeType = d.type;
        const isRootNode = nodeType === 'farm';
        
        // Reset node size
        if (nodeGroup.select('circle').size() > 0) {
          // For circle nodes
          const originalRadius = isRootNode ? 25 : 15;
          nodeGroup.select('circle')
            .transition()
            .duration(200)
            .attr('r', originalRadius);
        } else if (nodeGroup.select('rect').size() > 0) {
          // For rectangle nodes
          nodeGroup.select('rect')
            .transition()
            .duration(200)
            .attr('width', 40)
            .attr('height', 40)
            .attr('x', -20)
            .attr('y', -20);
        }
        
        // Reset all links
        d3.selectAll('.link')
          .transition()
          .duration(200)
          .style('opacity', 0.7)
          .style('stroke-width', 2);
        
        // Reset all nodes
        d3.selectAll('.node')
          .transition()
          .duration(200)
          .style('opacity', 1);
      });
      
      // Create force simulation with hierarchical layout
      this.simulation = d3.forceSimulation(this.nodes)
        // Link force with custom distance based on node types
        .force('link', d3.forceLink(this.links)
          .id(d => d.id)
          .distance(link => {
            // Get source and target nodes
            const source = this.nodes.find(n => n.id === link.source.id || n.id === link.source);
            const target = this.nodes.find(n => n.id === link.target.id || n.id === link.target);
            
            if (!source || !target) return 100;
            
            // Adjust distance based on node levels
            const sourceLevel = source.level || 0;
            const targetLevel = target.level || 0;
            
            // If nodes are on the same level, space them further apart horizontally
            if (sourceLevel === targetLevel) return 150;
            
            // If nodes are on different levels, use vertical spacing
            return 100;
          }))
        // Charge force for node repulsion
        .force('charge', d3.forceManyBody()
          .strength(d => {
            // Root node has stronger repulsion
            if (d.level === 0) return -800;
            // First level nodes have medium repulsion
            if (d.level === 1) return -400;
            // Other nodes have standard repulsion
            return -200;
          })
          .distanceMax(300)) // Limit the distance of effect for better performance
        // Center force to keep the graph centered
        .force('center', d3.forceCenter(width / 2, height / 2))
        // X positioning force - horizontal layout
        .force('x', d3.forceX(d => {
          // Root node in center
          if (d.level === 0) return width / 2;
          
          // Get all nodes at this level
          const levelNodes = this.nodes.filter(n => n.level === d.level);
          const index = levelNodes.indexOf(d);
          const count = levelNodes.length;
          
          // Calculate horizontal position based on node type and level
          const levelWidth = width * 0.8;
          const padding = width * 0.1;
          
          // If we have type-based positioning
          if (d.type) {
            // Group nodes by type
            const types = [...new Set(levelNodes.map(n => n.type))];
            const typeIndex = types.indexOf(d.type);
            const typeCount = types.length;
            
            // Position based on type group
            const typeSection = levelWidth / typeCount;
            const typeCenter = padding + typeSection * (typeIndex + 0.5);
            
            // Find nodes of this type
            const typeNodes = levelNodes.filter(n => n.type === d.type);
            const typeNodeIndex = typeNodes.indexOf(d);
            const typeNodeCount = typeNodes.length;
            
            // Distribute nodes of the same type around their type center
            const nodeSpacing = Math.min(60, typeSection / (typeNodeCount + 1));
            return typeCenter + (typeNodeIndex - (typeNodeCount - 1) / 2) * nodeSpacing;
          }
          
          // Default positioning if no type-based grouping
          return padding + levelWidth * (index + 1) / (count + 1);
        }).strength(0.3))
        // Y positioning force - vertical layout by level
        .force('y', d3.forceY(d => {
          // Position by level
          const levelSpacing = 120;
          return 80 + d.level * levelSpacing;
        }).strength(0.8))
        // Collision force to prevent node overlap
        .force('collision', d3.forceCollide(d => this.getNodeSize(d) + 5).strength(0.8));
      
      // Update positions on tick
      this.simulation.on('tick', () => {
        link
          .attr('x1', d => d.source.x)
          .attr('y1', d => d.source.y)
          .attr('x2', d => d.target.x)
          .attr('y2', d => d.target.y);
        
        node.attr('transform', d => `translate(${d.x},${d.y})`);
      });
    },
    
    processGraphData() {
      // Start with the root entity
      const rootNode = {
        id: this.rootEntity.id,
        name: this.rootEntity.name || this.rootEntity.code || this.rootEntity.lineId,
        type: this.rootEntity.type,
        level: 0,
        ...this.rootEntity
      };
      
      this.nodes = [rootNode];
      this.links = [];
      
      // Track processed nodes to avoid duplicates
      const processedNodeIds = new Set([rootNode.id]);
      
      // Process connections
      if (this.connections && this.connections.length) {
        // First level connections
        this.connections.forEach((connection, index) => {
          const targetNode = {
            id: connection.id,
            name: connection.name || connection.code || connection.lineId,
            type: connection.type,
            level: 1,
            ...connection
          };
          
          // Only add if not already processed
          if (!processedNodeIds.has(targetNode.id)) {
            this.nodes.push(targetNode);
            processedNodeIds.add(targetNode.id);
            
            this.links.push({
              source: rootNode.id,
              target: targetNode.id,
              value: 1,
              color: this.getLinkColor(rootNode, targetNode)
            });
            
            // Second level connections if available
            if (connection.connections && connection.connections.length) {
              connection.connections.forEach(subConnection => {
                const subNode = {
                  id: subConnection.id,
                  name: subConnection.name || subConnection.code || subConnection.lineId,
                  type: subConnection.type,
                  level: 2,
                  ...subConnection
                };
                
                // Only add if not already processed
                if (!processedNodeIds.has(subNode.id)) {
                  this.nodes.push(subNode);
                  processedNodeIds.add(subNode.id);
                  
                  this.links.push({
                    source: targetNode.id,
                    target: subNode.id,
                    value: 0.7,
                    color: this.getLinkColor(targetNode, subNode)
                  });
                  
                  // Third level connections if available
                  if (subConnection.connections && subConnection.connections.length) {
                    subConnection.connections.forEach(thirdLevelConnection => {
                      const thirdLevelNode = {
                        id: thirdLevelConnection.id,
                        name: thirdLevelConnection.name || thirdLevelConnection.code || thirdLevelConnection.lineId,
                        type: thirdLevelConnection.type,
                        level: 3,
                        ...thirdLevelConnection
                      };
                      
                      // Only add if not already processed
                      if (!processedNodeIds.has(thirdLevelNode.id)) {
                        this.nodes.push(thirdLevelNode);
                        processedNodeIds.add(thirdLevelNode.id);
                        
                        this.links.push({
                          source: subNode.id,
                          target: thirdLevelNode.id,
                          value: 0.5,
                          color: this.getLinkColor(subNode, thirdLevelNode)
                        });
                      } else {
                        // If node already exists, just add the link
                        const existingLink = this.links.find(link => 
                          (link.source === subNode.id && link.target === thirdLevelNode.id) || 
                          (link.source === thirdLevelNode.id && link.target === subNode.id)
                        );
                        
                        if (!existingLink) {
                          this.links.push({
                            source: subNode.id,
                            target: thirdLevelNode.id,
                            value: 0.5,
                            color: this.getLinkColor(subNode, thirdLevelNode)
                          });
                        }
                      }
                    });
                  }
                } else {
                  // If node already exists, just add the link if it doesn't exist
                  const existingLink = this.links.find(link => 
                    (link.source === targetNode.id && link.target === subNode.id) || 
                    (link.source === subNode.id && link.target === targetNode.id)
                  );
                  
                  if (!existingLink) {
                    this.links.push({
                      source: targetNode.id,
                      target: subNode.id,
                      value: 0.7,
                      color: this.getLinkColor(targetNode, subNode)
                    });
                  }
                }
              });
            }
          } else {
            // If node already exists, just add the link if it doesn't exist
            const existingLink = this.links.find(link => 
              (link.source === rootNode.id && link.target === targetNode.id) || 
              (link.source === targetNode.id && link.target === rootNode.id)
            );
            
            if (!existingLink) {
              this.links.push({
                source: rootNode.id,
                target: targetNode.id,
                value: 1,
                color: this.getLinkColor(rootNode, targetNode)
              });
            }
          }
        });
        
        // Add cross-connections between nodes of the same level
        this.addCrossConnections();
      }
    },
    
    addCrossConnections() {
      // Find nodes that might be related
      const woolLineNodes = this.nodes.filter(node => node.type === 'wool_line');
      const classerNodes = this.nodes.filter(node => node.type === 'classer');
      const brokerNodes = this.nodes.filter(node => node.type === 'broker');
      const farmNodes = this.nodes.filter(node => node.type === 'farm');
      
      // Connect classers who work with the same farm
      if (classerNodes.length > 1 && farmNodes.length > 0) {
        for (let i = 0; i < classerNodes.length; i++) {
          for (let j = i + 1; j < classerNodes.length; j++) {
            // Check if they share a farm
            const classerA = classerNodes[i];
            const classerB = classerNodes[j];
            
            const sharedFarm = farmNodes.find(farm => 
              this.links.some(link => link.source === classerA.id && link.target === farm.id) &&
              this.links.some(link => link.source === classerB.id && link.target === farm.id)
            );
            
            if (sharedFarm) {
              // Add a connection between the classers
              this.links.push({
                source: classerA.id,
                target: classerB.id,
                value: 0.3,
                color: '#999',
                dashed: true
              });
            }
          }
        }
      }
      
      // Connect wool lines from the same farm
      if (woolLineNodes.length > 1) {
        for (let i = 0; i < woolLineNodes.length; i++) {
          for (let j = i + 1; j < woolLineNodes.length; j++) {
            const lineA = woolLineNodes[i];
            const lineB = woolLineNodes[j];
            
            if (lineA.farmPIC && lineA.farmPIC === lineB.farmPIC) {
              // Add a connection between the wool lines
              this.links.push({
                source: lineA.id,
                target: lineB.id,
                value: 0.3,
                color: '#999',
                dashed: true
              });
            }
          }
        }
      }
    },
    
    getNodeSize(node) {
      // Root node is larger
      if (node.level === 0) return 20;
      // First level nodes are medium
      if (node.level === 1) return 15;
      // Second level nodes are smaller
      return 12;
    },
    
    getNodeColor(node) {
      const typeConfig = this.nodeTypes[node.type] || this.nodeTypes.default;
      return typeConfig.color;
    },
    
    getNodeIcon(node) {
      const typeConfig = this.nodeTypes[node.type] || this.nodeTypes.default;
      return typeConfig.icon;
    },
    
    getNodeLabel(node) {
      if (node.name) return node.name.length > 15 ? node.name.substring(0, 12) + '...' : node.name;
      if (node.code) return node.code;
      if (node.lineId) return node.lineId;
      return node.id;
    },
    
    getNodeLevel(node) {
      return node.level || 0;
    },
    
    getLinkColor(source, target) {
      // Use a gradient based on node types
      const sourceColor = this.getNodeColor(source);
      const targetColor = this.getNodeColor(target);
      
      // For now, just use a neutral color
      return '#999';
    },
    
    drag(simulation) {
      // Store a reference to the simulation for use in the drag functions
      const sim = simulation;
      
      function dragstarted(event) {
        // Check if simulation exists before using it
        if (sim && !event.active) {
          sim.alphaTarget(0.3).restart();
        }
        event.subject.fx = event.subject.x;
        event.subject.fy = event.subject.y;
      }
      
      function dragged(event) {
        event.subject.fx = event.x;
        event.subject.fy = event.y;
      }
      
      function dragended(event) {
        // Check if simulation exists before using it
        if (sim && !event.active) {
          sim.alphaTarget(0);
        }
        event.subject.fx = null;
        event.subject.fy = null;
      }
      
      return d3.drag()
        .on('start', dragstarted)
        .on('drag', dragged)
        .on('end', dragended);
    },
    
    showNodeDetails(node, event) {
      // Calculate modal position based on the event coordinates
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Get the position from the event or use node coordinates if available
      let posX = event ? event.clientX : (node.x || 0) + 30;
      let posY = event ? event.clientY : (node.y || 0) - 100;
      
      // Make sure modal stays within viewport bounds
      const modalWidth = 350; // Approximate modal width
      const modalHeight = 300; // Approximate modal height
      
      // Adjust horizontal position if needed
      if (posX + modalWidth > viewportWidth) {
        posX = Math.max(20, viewportWidth - modalWidth - 20);
      }
      
      // Ensure minimum distance from top
      posY = Math.max(20, posY);
      
      // If it would go off bottom, adjust upward
      if (posY + modalHeight > viewportHeight) {
        posY = Math.max(20, viewportHeight - modalHeight - 20);
      }
      
      // Set the modal position
      this.modalPosition = {
        x: posX,
        y: posY
      };
      
      // Set the selected node and show the modal
      this.selectedNode = node;
      this.showModal = true;
      
      // Log for debugging
      console.log('Node clicked, modal position:', this.modalPosition);
    },
    
    closeModal() {
      this.showModal = false;
      this.selectedNode = {};
    },
    
    formatNodeType(type) {
      if (!type) return 'Unknown';
      
      // Convert snake_case to Title Case
      return type
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },
    
    formatFieldName(key) {
      if (!key) return '';
      
      // First handle camelCase
      const spacedKey = key.replace(/([A-Z])/g, ' $1').trim();
      // Then handle snake_case
      return spacedKey
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },
    
    getNodeSource(type) {
      const sources = {
        'farm': 'Farm profiles',
        'wool_line': 'Wool line data',
        'classer': 'Classer register',
        'broker': 'Broker records',
        'auction_house': 'Auction catalog',
        'testing_lab': 'AWTA results',
        'shipping_company': 'Shipping records',
        'port': 'Port registry',
        'factory': 'Factory inputs',
        'brand': 'Brand records',
        'certification_body': 'Certification registry',
        'government_agency': 'Government database'
      };
      
      return sources[type] || 'System data';
    },
    
    isCommercialEntity(type) {
      const commercialEntityTypes = [
        'classer', 
        'broker', 
        'auction_house', 
        'testing_lab', 
        'shipping_company', 
        'factory', 
        'brand', 
        'certification_body', 
        'government_agency'
      ];
      
      return commercialEntityTypes.includes(type);
    },
    
    canNavigateToNode(node) {
      // Only allow navigation to certain node types
      const navigableTypes = [
        'farm', 
        'wool_line', 
        'classer', 
        'broker', 
        'auction_house', 
        'testing_lab', 
        'shipping_company', 
        'factory', 
        'brand'
      ];
      
      return navigableTypes.includes(node.type);
    },
    
    navigateToNode(node) {
      this.closeModal();
      this.$emit('navigate-to-node', node);
    }
  }
};
</script>

<style scoped>
.connections-graph {
  width: 100%;
  height: 100%;
}

.graph-container {
  width: 100%;
  height: 600px;
  background-color: #f9fafb;
  border-radius: 0.5rem;
  overflow: hidden;
}

/* Node detail modal styling */
.card-header {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.card-body {
  padding: 16px;
}

.detail-item {
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.4;
  color: #374151;
}

.detail-item strong {
  color: #111827;
  font-weight: 500;
  margin-right: 4px;
}

.card-source {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #f3f4f6;
  font-size: 12px;
  color: #6b7280;
}

.card-footer {
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  background-color: #f9fafb;
}

.details-btn {
  background: none;
  border: none;
  color: #3A895F;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.details-btn:hover {
  background: #e7eeea;
}
</style>
